import React, { useMemo } from 'react';
import { Box, Skeleton, Stack, Typography, useTheme } from '@mui/material';
import DataTable from 'react-data-table-component';

const defaultProgress = (
  <Stack spacing={1} sx={{ width: '100%', p: 2 }}>
    {Array.from({ length: 5 }).map((_, index) => (
      <Skeleton key={index} variant="rounded" height={54} />
    ))}
  </Stack>
);

const defaultNoData = (
  <Box sx={{ textAlign: 'center', py: 6 }}>
    <Typography variant="subtitle1" fontWeight={700} gutterBottom>
      Sin registros
    </Typography>
    <Typography variant="body2" color="text.secondary">
      No hay datos para mostrar.
    </Typography>
  </Box>
);

const normalizeColumns = (gridColumns) =>
  (gridColumns || []).map((col) => {
    const name = col.headerName ?? col.name ?? col.field ?? '';
    const sortable = Boolean(col.sortable);
    const selector =
      col.valueGetter
        ? (row) => col.valueGetter(undefined, row)
        : (row) => (col.field ? row?.[col.field] : undefined);
    const cell = col.renderCell
      ? (row) => col.renderCell({ row, value: selector(row) })
      : col.cell
        ? (row) => col.cell(row)
        : undefined;

    return {
      name,
      sortable,
      selector,
      cell,
      right: col.align === 'right',
      center: col.align === 'center' || col.headerAlign === 'center',
      wrap: true,
    };
  });

export default function AppDataTable({
  rows,
  columns,
  loading,
  noDataComponent = defaultNoData,
  progressComponent = defaultProgress,
  ...props
}) {
  const theme = useTheme();

  const normalizedColumns = useMemo(() => normalizeColumns(columns), [columns]);
  const customStyles = useMemo(
    () => ({
      table: {
        style: {
          backgroundColor: theme.palette.background.paper,
        },
      },
      headCells: {
        style: {
          backgroundColor: theme.palette.background.default,
          fontWeight: 700,
          fontSize: 13,
          textTransform: 'uppercase',
          borderBottom: `1px solid ${theme.palette.divider}`,
        },
      },
      rows: {
        style: {
          minHeight: 64,
          borderBottom: `1px solid ${theme.palette.divider}`,
        },
        highlightOnHoverStyle: {
          backgroundColor:
            theme.palette.mode === 'light'
              ? 'rgba(37, 99, 235, 0.04)'
              : 'rgba(91, 142, 255, 0.10)',
          outline: 'none',
        },
      },
      pagination: {
        style: {
          borderTop: `1px solid ${theme.palette.divider}`,
        },
      },
    }),
    [theme]
  );

  return (
    <Box
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <DataTable
        columns={normalizedColumns}
        data={rows || []}
        pagination
        highlightOnHover
        pointerOnHover
        dense
        progressPending={Boolean(loading)}
        progressComponent={progressComponent}
        noDataComponent={noDataComponent}
        customStyles={customStyles}
        {...props}
      />
    </Box>
  );
}

